/**
 * Insurance explain screen copy builders
 *
 * German timeline and headline templates for invoice, blocked, and Abtretung explain steps.
 */

import type {
  AbtretungExplainMeta,
  WithoutAbtretungPatientExplainMeta,
  WithoutAbtretungPhysiofitExplainMeta,
} from './config';
import { applyTemplate } from './copyUtils';

export const SHARED_INVOICE_TIMELINE = {
  step1Title: 'Starte & Trainiere unverbindlich',
  step1Body: 'Sollte der Kurs nichts für dich sein, kannst du jederzeit aufhören - komplett kostenlos und risikofrei.',
  step2Title: 'Wir reichen den Kurs für dich ein',
  step2BodyTemplate:
    'Du musst dich um nichts kümmern. Wir senden die Unterlagen für dich direkt an die {insuranceName}.',
} as const;

/**
 * Course-submission step wording for insurers that make the patient hand in the course
 * themselves (`isPatientSubmittingInsurer`, today the TK). Mirrors the drawer's `patientSubmits`
 * branch in `bnplCopy.ts`'s `buildBnplDrawerSteps`, reused here by the non-BNPL billing-detail
 * drawers (invoice, without-Abtretung variants) for the same submission step.
 *
 * @param {string} insuranceName Short display name of the insurer.
 * @return {Object} Title and body for the patient-submits timeline step.
 */
export function buildPatientSubmitsStepCopy(insuranceName: string): { title: string; body: string } {
  return {
    title: `Wir unterstützen bei der Kurseinreichung bei der ${insuranceName}`,
    body: `Wir begleiten dich bei der Kurseinreichung, du musst lediglich die Übermittlung an die ${insuranceName} übernehmen.`,
  };
}

const DEFAULT_BLOCKED_BODY =
  'Da wir aktuell keine Vereinbarung mit deiner Krankenkasse haben, kannst du Physiofit kostenfrei nutzen. Viel Spaß.';

export const DEFAULT_BLOCKED_EXPLAIN = {
  title: 'Du nutzt Physiofit kostenfrei',
  body: DEFAULT_BLOCKED_BODY,
} as const;

const ABTRETUNG_STEP2_BODY_TEMPLATE =
  'Du musst dich um nichts kümmern. Wir senden die Unterlagen für dich direkt an die {insuranceName} und rechnen den Kurs ab. Auf dich kommen zu keinem Zeitpunkt Kosten zu.';

/**
 * Builds Abtretung explain meta with insurer name interpolated into templates.
 *
 * @param {string} insuranceName Display name of the selected insurer.
 * @return {Object} Headline and timeline copy for the Abtretung overview screen.
 */
export function buildAbtretungExplainMeta(insuranceName: string): AbtretungExplainMeta {
  return {
    headlineTemplate: `Dein Kursablauf mit der ${insuranceName}`,
    timeline: {
      ...SHARED_INVOICE_TIMELINE,
      step2BodyTemplate: applyTemplate(ABTRETUNG_STEP2_BODY_TEMPLATE, { insuranceName }),
    },
  };
}

const WITHOUT_ABTRETUNG_PATIENT_STEP2_BODY_TEMPLATE =
  'Nach Kursabschluss senden wir deine Unterlagen für dich direkt an die {insuranceName}.';
const WITHOUT_ABTRETUNG_PATIENT_STEP3_BODY_TEMPLATE =
  'Wir halten dich auf dem Laufenden und melden uns, sobald deine Einreichung bei der {insuranceName} bearbeitet wird.';
const WITHOUT_ABTRETUNG_PATIENT_STEP4_BODY_TEMPLATE =
  'Die {insuranceName} überweist dir die Erstattung. Sobald das Geld bei dir ist, leitest du es einfach an uns weiter.';

/**
 * Builds without-Abtretung-patient explain meta with insurer name interpolated into templates.
 *
 * @param {string} insuranceName Display name of the selected insurer.
 * @return {Object} Headline and 4-step timeline copy for the without-Abtretung-patient overview screen.
 */
export function buildWithoutAbtretungPatientExplainMeta(insuranceName: string): WithoutAbtretungPatientExplainMeta {
  return {
    headlineTemplate: `Dein Kursablauf mit der ${insuranceName}`,
    timeline: {
      step1Title: SHARED_INVOICE_TIMELINE.step1Title,
      step1Body: SHARED_INVOICE_TIMELINE.step1Body,
      step2Title: 'Wir reichen deinen Kurs direkt ein',
      step2BodyTemplate: applyTemplate(WITHOUT_ABTRETUNG_PATIENT_STEP2_BODY_TEMPLATE, { insuranceName }),
      step3Title: 'Wir begleiten dich',
      step3BodyTemplate: applyTemplate(WITHOUT_ABTRETUNG_PATIENT_STEP3_BODY_TEMPLATE, { insuranceName }),
      step4Title: 'Du erhältst die Erstattung',
      step4BodyTemplate: applyTemplate(WITHOUT_ABTRETUNG_PATIENT_STEP4_BODY_TEMPLATE, { insuranceName }),
    },
  };
}

const WITHOUT_ABTRETUNG_PHYSIOFIT_STEP2_BODY_TEMPLATE =
  'Du musst dich um nichts kümmern. Wir senden die Unterlagen für dich direkt an die {insuranceName} und rechnen den Kurs ab. Auf dich kommen zu keinem Zeitpunkt Kosten zu.';

/**
 * Builds without-Abtretung-physiofit explain meta with insurer name interpolated into templates.
 * Same flow as the patient variant but without the final "forward the refund" step, since the
 * insurer pays Physiofit directly.
 *
 * @param {string} insuranceName Display name of the selected insurer.
 * @return {Object} Headline and 2-step timeline copy for the without-Abtretung-physiofit overview screen.
 */
export function buildWithoutAbtretungPhysiofitExplainMeta(insuranceName: string): WithoutAbtretungPhysiofitExplainMeta {
  return {
    headlineTemplate: `Dein Kursablauf mit der ${insuranceName}`,
    timeline: {
      step1Title: SHARED_INVOICE_TIMELINE.step1Title,
      step1Body: SHARED_INVOICE_TIMELINE.step1Body,
      step2Title: 'Wir reichen deinen Kurs direkt ein',
      step2BodyTemplate: applyTemplate(WITHOUT_ABTRETUNG_PHYSIOFIT_STEP2_BODY_TEMPLATE, { insuranceName }),
    },
  };
}
